/**
 * lib/assets.ts
 * ------------------------------------------------------------------
 * WHAT THE SCHOOL OWNS, AND WHAT IT IS STILL WORTH.
 *
 * A minibus, a laptop trolley, the science lab benches. Each was bought
 * once and is used for years, so charging the whole cost to the month it
 * was paid for makes that month look terrible and every month after it
 * better than it was — and the accounts stop describing how the school
 * actually runs. Depreciation spreads the cost across the years the thing
 * is actually used.
 *
 * THE REGISTER IS ALSO A CONTROL, NOT ONLY A CALCULATION. It is the only
 * record that says the minibus exists, who has it and what it cost. A
 * school without one cannot answer "is it still here?" — which is the
 * question an auditor asks, and the question a theft answers for you.
 *
 * STRAIGHT LINE, AND NOTHING ELSE FOR NOW. Cost less residual, spread
 * evenly across the useful life. Reducing balance exists and some schools
 * use it, but offering a method nobody has chosen invites picking one at
 * random, and switching method mid-life makes a comparison across years
 * meaningless.
 *
 * IT NEVER DEPRECIATES BELOW THE RESIDUAL. An asset held past its useful
 * life stays on the books at what it is reckoned to be worth; running the
 * charge on would turn a working minibus into a liability.
 *
 * PURE. Assets in, charges and carrying values out.
 * ------------------------------------------------------------------ */
import { round2 } from '@/lib/financeCore';

export const ASSET_COLLECTION = 'financeAssets';
export const assetCounterId = (year: string): string => `assets-${year}`;
export const formatAssetNo = (seq: number, year: string): string =>
  `FA/${year}/${String(seq).padStart(4, '0')}`;

export type AssetStatus = 'in_use' | 'disposed' | 'written_off';

export const ASSET_STATUS_LABEL: Record<AssetStatus, string> = {
  in_use: 'In use',
  disposed: 'Disposed of',
  written_off: 'Written off',
};

/**
 * The classes a school actually buys, with the lives it actually uses.
 *
 * Named rather than free-typed: a register where one person writes
 * "Vehicles" and another "Motor vehicle" cannot be summarised, and the
 * summary is the reason the register exists.
 */
export interface AssetClass {
  key: string;
  label: string;
  /** Years. The default, which any individual asset may override. */
  usefulLife: number;
}

export const ASSET_CLASSES: AssetClass[] = [
  { key: 'buildings', label: 'Buildings & improvements', usefulLife: 40 },
  { key: 'vehicles', label: 'Vehicles', usefulLife: 8 },
  { key: 'furniture', label: 'Furniture & fittings', usefulLife: 10 },
  { key: 'equipment', label: 'Equipment & machinery', usefulLife: 8 },
  { key: 'computers', label: 'Computers & IT', usefulLife: 4 },
  { key: 'books', label: 'Library books', usefulLife: 5 },
];

export interface FixedAsset {
  id: string;
  assetNo: string;
  description: string;
  classKey: string;
  /** When it was bought — the date depreciation runs from. */
  acquiredOn: string;
  cost: number;
  /** What it is expected to be worth at the end of its life. */
  residual: number;
  /** Years. Overrides the class default when the school knows better. */
  usefulLife: number;
  /** Where it is, and who is answerable for it. */
  location?: string;
  custodian?: string;
  serial?: string;
  /** The voucher or order it came in on, so the cost can be traced. */
  sourceRef?: string;
  status: AssetStatus;
  disposedOn?: string;
  /** What it was sold for. Nil for a write-off. */
  proceeds?: number;
  notes?: string;
  createdAt: string;
  createdBy?: string;
}

/** The amount to be spread — never the full cost. */
export const depreciableAmount = (a: Pick<FixedAsset, 'cost' | 'residual'>): number =>
  round2(Math.max(0, (Number(a.cost) || 0) - (Number(a.residual) || 0)));

/** The charge for one full year, straight line. */
export function annualCharge(a: Pick<FixedAsset, 'cost' | 'residual' | 'usefulLife'>): number {
  const life = Number(a.usefulLife) || 0;
  if (life <= 0) return 0;
  return round2(depreciableAmount(a) / life);
}

/** Whole months between two dates, which is how depreciation is counted. */
export function monthsBetween(from: string, to: string): number {
  const [fy, fm] = from.slice(0, 7).split('-').map(Number);
  const [ty, tm] = to.slice(0, 7).split('-').map(Number);
  if (!fy || !ty) return 0;
  return Math.max(0, (ty - fy) * 12 + (tm - fm));
}

export interface AssetPosition {
  asset: FixedAsset;
  /** Months the asset has been in use, capped at its life. */
  monthsHeld: number;
  /** Total charged since it was bought. */
  accumulated: number;
  /** Cost less accumulated depreciation. */
  carrying: number;
  /** The charge for the year being reported. */
  chargeForYear: number;
  /** True once it has reached its residual and stopped depreciating. */
  fullyDepreciated: boolean;
}

/**
 * Where an asset stands at a date.
 *
 * MONTHLY, NOT ANNUAL. A minibus bought in November has depreciated for
 * two months by year end, not a year — charging a full year would
 * overstate the expense by ten months and understate the asset by the
 * same, and the error persists for the whole life.
 *
 * The month of purchase counts, the month of the reporting date does
 * not: a school does not get a month's use out of an asset bought on the
 * 31st, and the convention has to be one thing consistently rather than
 * argued each time.
 */
export function assetPosition(
  a: FixedAsset,
  asOf: string,
  yearStart?: string,
): AssetPosition {
  const monthly = annualCharge(a) / 12;
  const lifeMonths = (Number(a.usefulLife) || 0) * 12;

  const end = a.status === 'in_use' ? asOf : (a.disposedOn ?? asOf);
  const monthsHeld = Math.min(lifeMonths, monthsBetween(a.acquiredOn, end));

  const accumulated = round2(Math.min(depreciableAmount(a), monthly * monthsHeld));
  const carrying = round2((Number(a.cost) || 0) - accumulated);

  let chargeForYear = 0;
  if (yearStart) {
    const atStart = Math.min(lifeMonths, monthsBetween(a.acquiredOn, yearStart));
    const before = round2(Math.min(depreciableAmount(a), monthly * atStart));
    chargeForYear = round2(accumulated - before);
  }

  return {
    asset: a,
    monthsHeld,
    accumulated,
    carrying,
    chargeForYear,
    fullyDepreciated: lifeMonths > 0 && monthsHeld >= lifeMonths,
  };
}

export interface AssetRegisterReport {
  positions: AssetPosition[];
  cost: number;
  accumulated: number;
  carrying: number;
  chargeForYear: number;
  byClass: {
    classKey: string;
    label: string;
    count: number;
    cost: number;
    carrying: number;
  }[];
  /** Assets still on the register that have nothing left to depreciate. */
  fullyDepreciated: AssetPosition[];
}

export function buildAssetRegister(
  assets: FixedAsset[],
  asOf: string,
  yearStart: string,
): AssetRegisterReport {
  /* A disposed asset leaves the register's totals but stays in the
     history — the register has to be able to answer what happened to the
     minibus, and an asset that simply vanishes is indistinguishable from
     one that was never recorded. */
  const live = assets.filter((a) => a.status === 'in_use');
  const positions = live
    .map((a) => assetPosition(a, asOf, yearStart))
    .sort((x, y) => x.asset.assetNo.localeCompare(y.asset.assetNo));

  const sum = (f: (p: AssetPosition) => number) =>
    round2(positions.reduce((n, p) => n + f(p), 0));

  const byClass = ASSET_CLASSES.map((c) => {
    const mine = positions.filter((p) => p.asset.classKey === c.key);
    return {
      classKey: c.key,
      label: c.label,
      count: mine.length,
      cost: round2(mine.reduce((n, p) => n + (Number(p.asset.cost) || 0), 0)),
      carrying: round2(mine.reduce((n, p) => n + p.carrying, 0)),
    };
  }).filter((c) => c.count > 0);

  return {
    positions,
    cost: sum((p) => Number(p.asset.cost) || 0),
    accumulated: sum((p) => p.accumulated),
    carrying: sum((p) => p.carrying),
    chargeForYear: sum((p) => p.chargeForYear),
    byClass,
    fullyDepreciated: positions.filter((p) => p.fullyDepreciated),
  };
}

/**
 * Profit or loss on disposal.
 *
 * Proceeds less what the asset was still carried at. A LOSS is not a
 * failure — it usually means the useful life was optimistic — but it
 * belongs in the accounts rather than being buried in the disposal, and a
 * recurring pattern of losses says the lives need revisiting.
 */
export function disposalResult(a: FixedAsset, asOf: string): {
  carrying: number; proceeds: number; result: number; isLoss: boolean;
} {
  const pos = assetPosition({ ...a, status: 'in_use' }, a.disposedOn ?? asOf);
  const proceeds = round2(Number(a.proceeds) || 0);
  const result = round2(proceeds - pos.carrying);
  return { carrying: pos.carrying, proceeds, result, isLoss: result < 0 };
}

export function assetProblem(a: Partial<FixedAsset>): string | null {
  if (!a.description?.trim()) return 'Say what the asset is.';
  if (!a.classKey) return 'Choose an asset class.';
  if (!a.acquiredOn) return 'When was it bought?';
  if (!(Number(a.cost) > 0)) return 'A cost above nil is required.';
  if (Number(a.residual) < 0) return 'A residual value cannot be negative.';
  if (Number(a.residual) > Number(a.cost)) {
    return 'The residual value cannot exceed what it cost.';
  }
  if (!(Number(a.usefulLife) > 0)) return 'A useful life in years is required.';
  return null;
}
